/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as MsDyn365 from '@msdyn365-commerce/core';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, Heading, IAlertProps, IModuleProps, INodeProps, ITelemetryContent, Modal, ModalBody, ModalFooter, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import { CookieContext } from '@msdyn365-commerce/runtime-internal/dist/types/utils/cookie-utils';
import classnames from 'classnames';
import * as React from 'react';
import * as GTM from '../../shared-utils/google-tag-manager/gtm';

import { CookieComplianceAcceptButton, CookieComplianceLinks } from './components';
import { IDlvryCookieComplianceConfig, IDlvryCookieComplianceProps } from './dlvry-cookie-compliance.props.autogenerated';
import { CookieComplianceAcceptEssentialButton } from './components/accept-essential-button';
import { IDlvryCookieComplianceData } from './dlvry-cookie-compliance.data';
import { ICookieContextWithEssential } from '../../shared-utils/cookie-utilities';
import { CookieComplianceCustomizeButton } from './components/customize-button';
import { CookieCategory } from './components/cookie-category';

export interface ICookieComplianceState {
    visible: boolean;
    showCustomizeModal?: boolean;
    isReloading?: boolean;

    functionalCookies?: boolean;
    analyticalCookies?: boolean;
    advertisingCookies?: boolean;
    securityCookies?: boolean;
}

/**
 * Cookie compliance view props interface.
 */
export interface ICookieComplianceViewProps extends IDlvryCookieComplianceProps<{}> {
    CookieComplianceBanner: IModuleProps;
    AlertProps: IAlertProps;
    Content: INodeProps;
    ButtonContainer: INodeProps;
    heading?: React.ReactNode;
    text?: React.ReactNode;
    links?: React.ReactNode;
    acceptButton?: React.ReactNode;
    acceptEssentialButton?: React.ReactNode;
    customizeButton?: React.ReactNode;
    customizeModal?: React.ReactElement;
    className: string;
    onDismiss(): void;
}

/**
 *
 * CookieCompliance component.
 * @extends {React.PureComponent<IDlvryCookieComplianceProps<IHeroConfig>>}
 */
class CookieCompliance extends React.PureComponent<IDlvryCookieComplianceProps<IDlvryCookieComplianceData>, ICookieComplianceState> {
    private readonly cookieRef: React.RefObject<HTMLInputElement>;

    private readonly hideBannerCookieName = '_msdyn365__hcb3_';

    private readonly telemetryContent: ITelemetryContent = getTelemetryObject(
        this.props.context.request.telemetryPageName!,
        this.props.friendlyName,
        this.props.telemetry
    );

    public constructor(props: IDlvryCookieComplianceProps<IDlvryCookieComplianceData>) {
        super(props);
        this.state = {
            showCustomizeModal: false,
            visible: false
        };
        this.cookieRef = React.createRef();
        this._onDismiss = this._onDismiss.bind(this);
    }

    public componentDidUpdate(prevProps: Readonly<IDlvryCookieComplianceProps<IDlvryCookieComplianceData>>, prevState: Readonly<ICookieComplianceState>, snapshot?: any): void {
        // Check if visible state has changed
        if (prevState.visible !== this.state.visible) {
            // If the visibility has changed, we can update the body class
            document.body.classList[this.state.visible ? 'add' : 'remove']('hide-scrollbar');
            window.consentVisible = this.state.visible;
        }
    }

    public componentDidMount(): void {
        const { context, config } = this.props;
        const cookies = context.request.cookies as ICookieContextWithEssential;
        const hideCookieBannerCookie = !!cookies.get(this.hideBannerCookieName, true).value;
        const savedConsentMode = GTM.getConsentMode();

        this.setState({
            functionalCookies: savedConsentMode?.functionality_storage === 'granted',
            analyticalCookies: savedConsentMode?.analytics_storage === 'granted',
            advertisingCookies: savedConsentMode?.ad_storage === 'granted',
            securityCookies: savedConsentMode?.security_storage === 'granted'
        });

        // Check if cookie compliance is disabled. If yes we can give consent to Clarity and GTM without showing the banner.
        if (!cookies._options.isConsentRequired) {
            if (config.clarity) {
                this.clarityConsent();
                this.claritySetIds();
            }

            GTM.consentAll();
            return;
        }

        // Show popup if consent mode is not defined yet. Reset all consents to be sure we start fresh
        if (!hideCookieBannerCookie || !savedConsentMode) {
            context.request.cookies.deleteConsentCookie();

            if (config.clarity) {
                this.clarityRevokeConsent();
            }

            GTM.consentNone();
            this.setState({ visible: true });
        } else {
            if (savedConsentMode.analytics_storage === 'granted') {
                this.clarityConsent();
                this.claritySetIds();
            } else {
                this.clarityRevokeConsent();
            }

            GTM.consent(savedConsentMode);
        }

        if (typeof window !== 'undefined') {
            window.changeConsent = () => {
                this.setState({ visible: true });
            };
        }
    }

    public render(): JSX.Element | null {
        const { config, resources, telemetry } = this.props;
        const {
            acceptCookiesButtonText,
            acceptCookiesAriaLabel,
            acceptEssentialCookiesButtonText,
            acceptEssentialCookiesAriaLabel
        } = resources;
        const { visible } = this.state;

        let moduleConfig: IDlvryCookieComplianceConfig;

        try {
            moduleConfig = config;
        } catch (error) {
            telemetry.error(`Something went wrong while rendering the alert module ------${error}`);
            return <div>{error}</div>;
        }

        if (!visible) {
            return <></>;
        }

        if (!MsDyn365.msdyn365Commerce.isBrowser) {
            return <></>;
        }

        const viewProps: Partial<ICookieComplianceViewProps> = {
            ...this.props,
            CookieComplianceBanner: {
                moduleProps: this.props,
                className: classnames('ms-cookie-compliance', moduleConfig.className),
                ref: this.cookieRef
            },
            AlertProps: {
                className: 'ms-cookie-compliance__container',
                color: 'white',
                fade: false
            },
            Content: {
                className: 'ms-cookie-compliance__content'
            },
            ButtonContainer: {
                className: 'ms-cookie-compliance__button-container'
            },
            heading: (
                <Heading
                    className='ms-cookie-compliance__heading'
                    text={(
                        <>
                            <span style={{ verticalAlign: 'middle' }}>{resources.cookieComplianceHeading}</span>
                            <img
                                className='ms-cookie-compliance__heading__icon'
                                src={MsDyn365.getAsset('icons/cookie_3.svg', this.props.context.request)}
                            />
                        </>
                    ) as unknown as string}
                />
            ),
            text: moduleConfig.content && (
                <MsDyn365.RichTextComponent
                    className='ms-cookie-compliance__text'
                    text={moduleConfig.content}
                    editProps={{ onEdit: this.handleContentChange, requestContext: this.props.context.request }}
                />
            ),
            links: ArrayExtensions.hasElements(moduleConfig.actionLinks) && (
                <CookieComplianceLinks
                    links={moduleConfig.actionLinks}
                    onTextChange={this.handleLinkTextChange}
                    requestContext={this.props.context.request}
                    telemetryContent={this.telemetryContent}
                />
            ),
            acceptButton: (
                <CookieComplianceAcceptButton
                    reloading={this.state.isReloading}
                    onClose={this.handleAcceptAllCookies}
                    acceptCookiesButtonText={acceptCookiesButtonText}
                    acceptCookiesButtonAriaLabel={acceptCookiesAriaLabel}
                    telemetryContent={this.telemetryContent}
                />
            ),
            acceptEssentialButton: (
                <CookieComplianceAcceptEssentialButton
                    reloading={this.state.isReloading}
                    onClose={this.handleRejectCookies}
                    acceptEssentialCookiesButtonText={acceptEssentialCookiesButtonText}
                    acceptEssentialCookiesButtonAriaLabel={acceptEssentialCookiesAriaLabel}
                    telemetryContent={this.telemetryContent}
                />
            ),
            customizeButton: (
                <CookieComplianceCustomizeButton
                    reloading={this.state.isReloading}
                    onClose={() => this.setState({ showCustomizeModal: true })}
                    customizeCookiesButtonText={resources.customizeCookiesButtonText}
                    customizeCookiesButtonAriaLabel={resources.customizeCookiesButtonAriaLabel}
                    telemetryContent={this.telemetryContent}
                />
            ),
            customizeModal: this.renderCustomizeModal()
        };

        return this.props.renderView(viewProps as ICookieComplianceViewProps) as React.ReactElement;
    }

    /**
     * Handles Link content change event.
     * @param linkIndex - Index of link in linkarray.
     * @returns Void.
     */
    public handleLinkTextChange = (linkIndex: number) => (event: MsDyn365.ContentEditableEvent): void => {
        if (this.props.config.actionLinks?.[linkIndex]) {
            this.props.config.actionLinks[linkIndex].linkText = event.target.value;
        }
    };

    /**
     * Handles content change event.
     * @param event - Event.
     */
    public handleContentChange = (event: MsDyn365.ContentEditableEvent): void => {
        this.props.config.content = event.target.value;
    };

    private _onDismiss(consentAll: boolean): void {
        const { context, config } = this.props;
        const cookies = context.request.cookies as CookieContext;

        // Set cookie to hide the banner
        cookies.set(this.hideBannerCookieName, Date.now(), { path: '/', maxAge: 31536000, isEssential: true });

        if (consentAll && cookies) {
            cookies.setConsentCookie();

            if (config.clarity) {
                this.clarityConsent();
                this.claritySetIds();
            }

            GTM.consentAll();

            location.reload();
            this.setState({ isReloading: true });
            return;
        }

        const { functionalCookies, analyticalCookies, advertisingCookies, securityCookies } = this.state;

        if (!consentAll && cookies) {
            if (config.clarity) {
                if (analyticalCookies) {
                    this.clarityConsent();
                    this.claritySetIds();
                } else {
                    this.clarityRevokeConsent();
                }
            }

            GTM.consent({
                ad_storage: advertisingCookies ? 'granted' : 'denied',
                analytics_storage: analyticalCookies ? 'granted' : 'denied',
                functionality_storage: functionalCookies ? 'granted' : 'denied',
                security_storage: securityCookies ? 'granted' : 'denied',
                personalization_storage: functionalCookies ? 'granted' : 'denied'
            });

            if (functionalCookies !== cookies.isConsentGiven()) {
                functionalCookies ? cookies.setConsentCookie() : cookies.deleteConsentCookie();
                location.reload();
                this.setState({ isReloading: true });
                return;
            }
        }

        this.setState({ visible: false });
    }

    private renderCustomizeModal = (): React.ReactElement => {
        if (!this.state.showCustomizeModal) {
            return <></>;
        }

        const { resources } = this.props;
        const className = 'ms-cookie-compliance__customize-modal';

        return (
            <Modal className={className} isOpen={this.state.showCustomizeModal} toggle={() => this.setState({ showCustomizeModal: false })} zIndex={6001}>
                <ModalHeader className={`${className}__header`} toggle={() => this.setState({ showCustomizeModal: false })}>
                     <span>{resources.customizeCookiesModalHeading}</span>
                </ModalHeader>

                <ModalBody className={`${className}__body`}>
                    <div className={`${className}__description`}>
                        {resources.customizeCookiesModalDescription}
                    </div>

                    <CookieCategory
                        baseClassName={className}
                        header={resources.customizeCookiesNecessaryLabel}
                        description={resources.customizeCookiesNecessaryDescription}
                        disabled
                        checked
                    />
                    <CookieCategory
                        baseClassName={className}
                        header={resources.customizeCookiesFunctionalLabel}
                        description={resources.customizeCookiesFunctionalDescription}
                        checked={this.state.functionalCookies}
                        onChange={(checked) => this.setState({ functionalCookies: checked })}
                    />
                    <CookieCategory
                        baseClassName={className}
                        header={resources.customizeCookiesAnalyticsLabel}
                        description={resources.customizeCookiesAnalyticsDescription}
                        checked={this.state.analyticalCookies}
                        onChange={(checked) => this.setState({ analyticalCookies: checked })}
                    />
                    <CookieCategory
                        baseClassName={className}
                        header={resources.customizeCookiesAdvertisingLabel}
                        description={resources.customizeCookiesAdvertisingDescription}
                        checked={this.state.advertisingCookies}
                        onChange={(checked) => this.setState({ advertisingCookies: checked })}
                    />
                    <CookieCategory
                        baseClassName={className}
                        header={resources.customizeCookiesSecurityLabel}
                        description={resources.customizeCookiesSecurityDescription}
                        checked={this.state.securityCookies}
                        onChange={(checked) => this.setState({ securityCookies: checked })}
                    />
                </ModalBody>

                <ModalFooter className={`${className}__footer`}>
                    <div className={`${className}__footer-buttons`}>
                        <button className={`${className}__footer-button secondary`} onClick={this.handleRejectCookies}>
                            {resources.customizeCookiesModalRejectButtonText}
                        </button>
                        <button className={`${className}__footer-button primary`} onClick={this.handleSaveCookies}>
                            {resources.customizeCookiesModalSaveButtonText}
                        </button>
                        <button className={`${className}__footer-button primary`} onClick={this.handleAcceptAllCookies}>
                            {resources.customizeCookiesModalAcceptAllButtonText}
                        </button>
                    </div>
                </ModalFooter>
            </Modal>
        );
    };

    private handleRejectCookies = (): void => {
        this.setState({
            showCustomizeModal: false,
            functionalCookies: false,
            analyticalCookies: false,
            advertisingCookies: false,
            securityCookies: false
        }, () => this._onDismiss(false));
    };

    private handleSaveCookies = (): void => {
        this.setState({ showCustomizeModal: false });
        this._onDismiss(false);
    };

    private handleAcceptAllCookies = (): void => {
        this.setState({
            showCustomizeModal: false,
            functionalCookies: true,
            analyticalCookies: true,
            advertisingCookies: true,
            securityCookies: true
        }, () => this._onDismiss(true));
    };

    private clarityConsent = (): void => {
        if (typeof window === 'undefined' || !(window as any).clarity) {
            return;
        }

        try {
            // Tell Clarity that the user has consented to all cookies
            (window as any).clarity('consent');
        } catch (error) {
            console.error('Error while setting clarity consent cookie', error);
        }
    };

    private clarityRevokeConsent = (): void => {
        if (typeof window === 'undefined' || !(window as any).clarity) {
            return;
        }

        try {
            // Tell Clarity that the user has revoked consent to all cookies
            (window as any).clarity('consent', false);
        } catch (error) {
            console.error('Error while revoking clarity consent cookie', error);
        }
    };

    private claritySetIds = (): void => {
        if (typeof window === 'undefined' || !(window as any).clarity) {
            return;
        }

        try {
            const { customerInformation, organizationInformation } = this.props.data;

            if (customerInformation.result?.AccountNumber) {
                (window as any).clarity('set', 'customerId', customerInformation.result.AccountNumber);
            }

            if (organizationInformation.result?.AccountNumber) {
                (window as any).clarity('set', 'organizationCustomerId', organizationInformation.result.AccountNumber);
            }
        } catch (error) {
            console.error('Error while setting clarity ids', error);
        }
    };
}

export default CookieCompliance;
